import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogOut, RefreshCw, Plus, Trash2, Eye, EyeOff, Layers, MessageSquare } from "lucide-react";
import { LogoBadge } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cd_admin_token";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-ink/20 py-3.5 font-sans text-lg text-ink placeholder-ink/35 focus:outline-none focus:border-magenta transition-colors";

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-reviews-login-form">
        <p className="font-display text-xs uppercase tracking-[0.3em] text-magenta mb-4">Admin · Reviews</p>
        <h1 className="font-serif font-light text-4xl text-ink">Manage reviews</h1>
        <div className="mt-10 space-y-6">
          <input data-testid="admin-reviews-email" type="email" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-reviews-password" type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" disabled={loading} data-testid="admin-reviews-login-submit" className="rounded-full brand-gradient px-8 py-3.5 font-display text-xs uppercase tracking-[0.14em] font-bold text-white hover:scale-105 transition-transform disabled:opacity-60">
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminReviews() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [editions, setEditions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeEdition, setActiveEdition] = useState(null);
  const [newEdition, setNewEdition] = useState({ name: "", subtitle: "" });
  const [newReview, setNewReview] = useState({ first_name: "", institution: "", experience: "" });

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

  const handleAuthError = (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
      toast.error("Session expired. Please sign in again.");
      return true;
    }
    return false;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [ed, rv] = await Promise.all([
        axios.get(`${API}/editions`),
        axios.get(`${API}/admin/reviews`, authHeader()),
      ]);
      setEditions(ed.data);
      setReviews(rv.data);
      if (!activeEdition && ed.data.length) setActiveEdition(ed.data[0].id);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Could not load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, [authed]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  const addEdition = async (e) => {
    e.preventDefault();
    if (!newEdition.name.trim()) { toast.error("Give the edition a name."); return; }
    try {
      const { data } = await axios.post(`${API}/admin/editions`, newEdition, authHeader());
      setEditions((p) => [...p, data]);
      setActiveEdition(data.id);
      setNewEdition({ name: "", subtitle: "" });
      toast.success("Edition added.");
    } catch (err) { if (!handleAuthError(err)) toast.error("Could not add edition."); }
  };

  const deleteEdition = async (id) => {
    if (!window.confirm("Delete this edition and ALL its reviews?")) return;
    try {
      await axios.delete(`${API}/admin/editions/${id}`, authHeader());
      setEditions((p) => p.filter((e) => e.id !== id));
      setReviews((p) => p.filter((r) => r.edition_id !== id));
      if (activeEdition === id) setActiveEdition(editions.find((e) => e.id !== id)?.id || null);
      toast.success("Edition deleted.");
    } catch (err) { if (!handleAuthError(err)) toast.error("Could not delete edition."); }
  };

  const addReview = async (e) => {
    e.preventDefault();
    if (!newReview.first_name.trim() || !newReview.institution.trim() || !newReview.experience.trim()) {
      toast.error("Fill in name, school and experience."); return;
    }
    try {
      const { data } = await axios.post(`${API}/admin/reviews`, { edition_id: activeEdition, ...newReview }, authHeader());
      setReviews((p) => [data, ...p]);
      setNewReview({ first_name: "", institution: "", experience: "" });
      toast.success("Review added.");
    } catch (err) { if (!handleAuthError(err)) toast.error("Could not add review."); }
  };

  const toggleApprove = async (r) => {
    try {
      const { data } = await axios.patch(`${API}/admin/reviews/${r.id}`, { approved: !r.approved }, authHeader());
      setReviews((p) => p.map((x) => (x.id === r.id ? data : x)));
    } catch (err) { if (!handleAuthError(err)) toast.error("Could not update review."); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, authHeader());
      setReviews((p) => p.filter((r) => r.id !== id));
      toast.success("Review deleted.");
    } catch (err) { if (!handleAuthError(err)) toast.error("Could not delete review."); }
  };

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const inputCls = "w-full bg-transparent border-b border-ink/20 py-3 font-sans text-base text-ink placeholder-ink/35 focus:outline-none focus:border-magenta transition-colors";
  const editionReviews = reviews.filter((r) => r.edition_id === activeEdition);

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-reviews-dashboard">
      <div className="sticky top-0 z-20 glass-light border-b border-ink/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/home"><LogoBadge className="!bg-transparent !px-0 !py-0" /></Link>
          <div className="flex items-center gap-2">
            <Link to="/reviews" data-testid="view-public-reviews" className="hidden sm:inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 font-display text-xs uppercase tracking-[0.12em] text-ink/70 hover:border-magenta hover:text-magenta transition-colors">
              View Page
            </Link>
            <button onClick={load} data-testid="admin-reviews-refresh" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 font-display text-xs uppercase tracking-[0.12em] text-ink/70 hover:border-magenta hover:text-magenta transition-colors">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-reviews-logout" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 font-display text-xs uppercase tracking-[0.12em] text-ink/70 hover:border-magenta hover:text-magenta transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-8 grid lg:grid-cols-[300px_1fr] gap-8">
        {/* Sidebar: editions */}
        <aside>
          <div className="flex items-center gap-2 mb-4">
            <Layers size={18} className="text-magenta" />
            <h2 className="font-serif font-light text-2xl text-ink">Editions</h2>
          </div>
          <div className="space-y-2" data-testid="admin-editions-list">
            {editions.map((e) => (
              <div key={e.id} className={`group flex items-center justify-between rounded-2xl border px-4 py-3 cursor-pointer transition-all ${activeEdition === e.id ? "border-magenta bg-white card-shadow" : "border-ink/10 hover:border-ink/30"}`} onClick={() => setActiveEdition(e.id)} data-testid={`admin-edition-${e.id}`}>
                <div>
                  <p className="font-display text-sm font-bold text-ink">{e.name}</p>
                  <p className="font-sans text-xs text-ink/50">{reviews.filter((r) => r.edition_id === e.id).length} reviews</p>
                </div>
                <button onClick={(ev) => { ev.stopPropagation(); deleteEdition(e.id); }} data-testid={`delete-edition-${e.id}`} className="opacity-0 group-hover:opacity-100 text-ink/30 hover:text-magenta transition-all">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <form onSubmit={addEdition} className="mt-6 rounded-2xl border border-dashed border-ink/20 p-4" data-testid="add-edition-form">
            <p className="font-display text-xs uppercase tracking-[0.16em] text-ink/40 mb-2">Add edition</p>
            <input data-testid="new-edition-name" className={inputCls} placeholder="Edition name" value={newEdition.name} onChange={(e) => setNewEdition((f) => ({ ...f, name: e.target.value }))} />
            <input data-testid="new-edition-subtitle" className={`${inputCls} mt-2`} placeholder="Subtitle (optional)" value={newEdition.subtitle} onChange={(e) => setNewEdition((f) => ({ ...f, subtitle: e.target.value }))} />
            <button type="submit" data-testid="add-edition-submit" className="mt-4 inline-flex items-center gap-2 rounded-full brand-gradient px-5 py-2.5 font-display text-xs uppercase tracking-[0.12em] font-bold text-white hover:scale-105 transition-transform">
              <Plus size={14} /> Add
            </button>
          </form>
        </aside>

        {/* Main: reviews for active edition */}
        <main>
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare size={20} className="text-magenta" />
            <h1 className="font-serif font-light text-3xl md:text-4xl text-ink">
              {editions.find((e) => e.id === activeEdition)?.name || "Reviews"}
            </h1>
            <span className="rounded-full brand-gradient px-3 py-1 font-display text-xs font-bold text-white" data-testid="admin-reviews-count">{editionReviews.length}</span>
          </div>

          {/* Add review */}
          <form onSubmit={addReview} className="rounded-3xl border border-ink/10 bg-white card-shadow p-6 mb-8" data-testid="admin-add-review-form">
            <p className="font-display text-xs uppercase tracking-[0.16em] text-magenta mb-4">Add a review to this edition</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="admin-review-first-name" className={inputCls} placeholder="First name" value={newReview.first_name} onChange={(e) => setNewReview((f) => ({ ...f, first_name: e.target.value }))} />
              <input data-testid="admin-review-institution" className={inputCls} placeholder="School / Institution" value={newReview.institution} onChange={(e) => setNewReview((f) => ({ ...f, institution: e.target.value }))} />
            </div>
            <textarea data-testid="admin-review-experience" rows={3} className={`${inputCls} mt-4 resize-none`} placeholder="Their experience..." value={newReview.experience} onChange={(e) => setNewReview((f) => ({ ...f, experience: e.target.value }))} />
            <button type="submit" disabled={!activeEdition} data-testid="admin-add-review-submit" className="mt-5 inline-flex items-center gap-2 rounded-full brand-gradient px-6 py-2.5 font-display text-xs uppercase tracking-[0.12em] font-bold text-white hover:scale-105 transition-transform disabled:opacity-50">
              <Plus size={14} /> Add review
            </button>
          </form>

          <div className="space-y-4" data-testid="admin-reviews-list">
            {editionReviews.length === 0 && (
              <p className="text-center text-ink/40 font-serif italic py-10">No reviews for this edition yet.</p>
            )}
            {editionReviews.map((r) => (
              <div key={r.id} className={`rounded-2xl border p-5 ${r.approved ? "border-ink/10 bg-white" : "border-coral/40 bg-coral/5"}`} data-testid={`admin-review-${r.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-sm font-bold text-ink">{r.first_name} <span className="font-sans font-normal text-ink/50">· {r.institution}</span></p>
                    <p className="mt-2 font-serif italic text-ink/80 leading-relaxed">“{r.experience}”</p>
                    {!r.approved && <span className="mt-2 inline-block rounded-full bg-coral/15 px-2.5 py-0.5 font-display text-[0.6rem] uppercase tracking-wider text-coral font-bold">Hidden</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => toggleApprove(r)} title={r.approved ? "Hide from page" : "Show on page"} data-testid={`toggle-approve-${r.id}`} className="text-ink/40 hover:text-magenta transition-colors">
                      {r.approved ? <Eye size={18} /> : <EyeOff size={18} />}
                    </button>
                    <button onClick={() => deleteReview(r.id)} title="Delete" data-testid={`delete-review-${r.id}`} className="text-ink/40 hover:text-magenta transition-colors">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}
